// Split out of src/components/Onboarding.jsx (docs/FILE_SPLIT_RUN1_SPEC.md, move-only).
// Onboarding.jsx re-exports everything it exported before, so existing imports keep working.

import { CopyrightFooter, Icon, LittleBankersHeaderMark } from "../Common.jsx";
import { apiRequestPasscodeRecovery, apiResetPasscode } from "../../lib/api.js";
import { genPasscode, PASSCODE_RE } from "../../lib/constants.js";
import { t as ct } from "../../lib/content.js";

const { useState, useEffect, useCallback, useMemo, useRef, useId } = React;

// Inline "forgot your passcode?" request form shown on the Join tab. Only needs an email
// address — the household code is read from the Join tab's own input above it, so the
// person doesn't have to type it twice. Deliberately shows the same success message
// whether or not the email actually matched the household's owner on file (the server
// enforces this too; see worker/handlers/recovery.js) so this can never be used to check
// whether a code is real or fish for the owner's email address.
function PasscodeRecoveryRequest({ code }) {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const codeValid = /^[A-Z0-9]{6}$/.test(code);

  async function submit() {
    setErr("");
    if (!codeValid) { setErr("Enter your 6-digit household code above first."); return; }
    if (!emailValid) { setErr("Enter the email address this household was set up with."); return; }
    setSending(true);
    try {
      await apiRequestPasscodeRecovery(code, email.trim());
      setSent(true);
    } catch (e) {
      setErr(e.rateLimited ? e.message : "Couldn't send that right now — try again shortly.");
    } finally {
      setSending(false);
    }
  }

  if (sent) {
    return (
      <div className="border-t border-b border-slate-200 text-xs text-slate-600 py-2.5 mb-3">
        If that email matches this household's owner, a reset link is on its way — check your inbox (and spam folder). The link expires in 30 minutes.
      </div>
    );
  }

  return (
    <div className="bg-slate-50 rounded-xl p-3 mb-3">
      <p className="text-xs text-slate-500 mb-2">{ct("onboarding.recovery.email_hint", "Enter the email address this household was set up with — we'll send a link to reset the passcode. This doesn't affect the Parent PIN.")}</p>
      <input
        type="email"
        inputMode="email"
        className="border border-slate-200 rounded-lg px-3 py-2 text-sm w-full mb-2"
        placeholder="Owner's email address"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        autoComplete="email"
      />
      {err && <div className="text-rose-600 text-xs font-semibold mb-2">{err}</div>}
      <button disabled={sending} className="w-full bg-slate-900 text-white font-bold text-xs rounded-lg py-2 disabled:opacity-50" onClick={submit}>
        {sending ? "Sending…" : "Send reset link"}
      </button>
    </div>
  );
}

// Full-screen destination for the emailed recovery link: littlebankers.uk/?recover=CODE&token=...
// Routed here directly from main.jsx, bypassing the invite gate (a returning owner isn't a
// new visitor). Requires an active choice of new passcode — nothing is pre-filled and
// ready to submit, so a stray tap can't silently set a passcode the person never saw.
// "Generate one for me" fills the field but still requires reviewing it and tapping
// Reset. Passcode only: this never touches the Parent PIN, which stays a separate,
// PIN-gated flow in Settings.
export function PasscodeReset() {
  const params = new URLSearchParams(window.location.search);
  const code = (params.get("recover") || "").toUpperCase();
  const token = params.get("token") || "";
  const codeValid = /^[A-Z0-9]{6}$/.test(code);

  const [newPasscode, setNewPasscode] = useState("");
  const [showPasscode, setShowPasscode] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  async function submit() {
    setError("");
    if (!codeValid || !token) { setError("This recovery link looks incomplete — request a new one."); return; }
    if (!PASSCODE_RE.test(newPasscode)) { setError("Passcode must be 8-30 characters: letters, numbers, or symbols, no spaces."); return; }
    setBusy(true);
    try {
      await apiResetPasscode(code, token, newPasscode);
      setDone(true);
    } catch (e) {
      setError(e.rateLimited ? e.message : (e.message || "Couldn't reset the passcode — the link may have expired."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="relative min-h-screen min-h-[100dvh] flex flex-col p-4" style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 16px)" }}>
      <div className="flex-1 flex items-center justify-center">
        <div className="bg-white rounded-lg border border-slate-200 p-6 w-full max-w-sm md:max-w-[560px]">
          <div className="mb-3 flex justify-center"><LittleBankersHeaderMark /></div>
          {!codeValid || !token ? (
            <div className="text-center">
              <div className="font-bold text-slate-800 mb-1">{ct("onboarding.recovery.link_invalid_heading", "Recovery link incomplete")}</div>
              <p className="text-xs text-slate-500 mb-4">{ct("onboarding.recovery.link_invalid_body", 'This link is missing its code or token. Go back to the app and use "Forgot your passcode?" on the Join tab to request a fresh one.')}</p>
              <a href="/" className="inline-block bg-slate-900 text-white font-bold text-sm rounded-xl py-2.5 px-5">{ct("onboarding.recovery.back_button", "Back to Little Bankers")}</a>
            </div>
          ) : done ? (
            <div className="text-center">
              <div className="mb-2 flex justify-center"><Icon id="ic-celebrate" size={36} hue="pink" /></div>
              <div className="font-bold text-slate-800 mb-1">{ct("onboarding.recovery.success_heading", "All set")}</div>
              <p className="text-xs text-slate-500 mb-4">{ct("onboarding.recovery.success_body", "Your new household passcode is ready. Use it on the Join tab to link this (or any) device to household {code}.", { code })}</p>
              <div className="bg-slate-50 rounded-xl px-3 py-3 mb-4">
                <div className="text-xs font-medium text-slate-500 mb-1">{ct("onboarding.new_passcode_label", "New passcode")}</div>
                <div className="font-mono font-semibold text-sm text-slate-800 break-all">{newPasscode}</div>
              </div>
              <a href="/" className="inline-block w-full bg-slate-900 text-white font-bold text-sm rounded-xl py-2.5">{ct("onboarding.recovery.go_button", "Go to Little Bankers")}</a>
            </div>
          ) : (
            <div>
              <div className="font-bold text-slate-800 mb-1 text-center">{ct("onboarding.recovery.form_heading", "Reset your passcode")}</div>
              <p className="text-xs text-slate-500 mb-4 text-center">{ct("onboarding.recovery.form_subheading", "Household {code}. Choose a new passcode below.", { code })}</p>
              <div className="relative mb-2">
                <input
                  className="border border-slate-200 rounded-lg pl-3 pr-16 py-2 text-sm w-full font-mono"
                  type={showPasscode ? "text" : "password"}
                  maxLength={30}
                  placeholder="New household passcode"
                  value={newPasscode}
                  onChange={(e) => setNewPasscode(e.target.value)}
                />
                <button type="button" className="absolute right-1 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400 px-2 py-1" onClick={() => setShowPasscode((v) => !v)} tabIndex={-1}>
                  {showPasscode ? "Hide" : "Show"}
                </button>
              </div>
              <button type="button" className="text-xs font-bold text-slate-900 underline mb-4" onClick={() => setNewPasscode(genPasscode())}>
                Generate one for me
              </button>
              {error && <div className="bg-rose-50 border border-rose-200 text-rose-700 text-xs font-semibold rounded-lg px-3 py-2 mb-3">{error}</div>}
              <button disabled={busy} className="w-full bg-slate-900 text-white font-bold text-sm rounded-xl py-2.5 disabled:opacity-50" onClick={submit}>
                {busy ? "Resetting…" : "Reset passcode"}
              </button>
            </div>
          )}
        </div>
      </div>
      <CopyrightFooter />
    </div>
  );
}

export { PasscodeRecoveryRequest };
